export type MarketingConsentState = 'granted' | 'denied' | 'unknown'

type CookieHub = {
  hasAnswered?: () => boolean
  hasConsented?: (category: string) => boolean
}

/**
 * @description https://docs.cookiehub.com/advanced/javascript-api#public-methods
 * DOM events are available from CookieHub 2.8.13 onward.
 */
const COOKIEHUB_EVENTS = [
  'cookiehub_onInitialise',
  'cookiehub_onStatusChange',
  'cookiehub_onAllow',
  'cookiehub_onRevoke',
]

const POLL_INTERVAL_MS = 50
const POLL_TIMEOUT_MS = 10_000

const getCookieHub = (): CookieHub | undefined =>
  typeof window === 'undefined'
    ? undefined
    : (window as Window & { cookiehub?: CookieHub }).cookiehub

export const getMarketingConsent = (): MarketingConsentState => {
  try {
    const cookiehub = getCookieHub()

    if (!cookiehub?.hasAnswered?.()) return 'unknown'
    if (typeof cookiehub.hasConsented !== 'function') return 'unknown'

    return cookiehub.hasConsented('marketing') ? 'granted' : 'denied'
  } catch {
    return 'unknown'
  }
}

export const subscribeMarketingConsent = (
  onChange: (state: MarketingConsentState) => void
): (() => void) => {
  if (typeof window === 'undefined') return () => {}

  let disposed = false
  let lastEmitted: MarketingConsentState | undefined
  let pollTimer: ReturnType<typeof setInterval> | undefined
  let pollDeadline: ReturnType<typeof setTimeout> | undefined

  const stopPolling = () => {
    if (pollTimer) clearInterval(pollTimer)
    if (pollDeadline) clearTimeout(pollDeadline)
    pollTimer = undefined
    pollDeadline = undefined
  }

  const emit = () => {
    if (disposed) return

    const state = getMarketingConsent()
    if (state !== 'unknown') stopPolling()
    if (state === lastEmitted) return

    lastEmitted = state
    onChange(state)
  }

  COOKIEHUB_EVENTS.forEach((name) => document.addEventListener(name, emit))

  // Safety net for CookieHub builds older than 2.8.13, which do not emit the
  // events above. Bounded, so it can never poll for the life of the page.
  if (getMarketingConsent() === 'unknown') {
    pollTimer = setInterval(emit, POLL_INTERVAL_MS)
    pollDeadline = setTimeout(stopPolling, POLL_TIMEOUT_MS)
  }

  return () => {
    disposed = true
    stopPolling()
    COOKIEHUB_EVENTS.forEach((name) => document.removeEventListener(name, emit))
  }
}
